import ExcelJS from "exceljs";
import { excelHeader } from "../exporter/header";
import { normalizeExcelValue } from "../utils/normalize-excel-value";
const UNIQUE_KEY_HEADERS = [
    "관리코드",
    "위치",
    "단가",
    "상태",
];
const DIFF_METADATA_HEADERS = ["데이터 구분", "행 번호", "고유 키"];
const PLACEHOLDER_NOTE_COLUMN_INDEX = 0;
export function diffWorkbook(oldData, newData) {
    const oldRows = buildSnapshotMap(oldData);
    const newRows = buildSnapshotMap(newData);
    const diffEntries = collectDiffEntries(oldRows, newRows);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("DIFF");
    // DIFF 시트 헤더를 생성
    configureDiffHeader(sheet);
    if (diffEntries.length === 0) {
        sheet.addRow(["INFO", "-", "-", "두 데이터 세트 간 차이점이 없습니다."]);
        return workbook;
    }
    // 고유 키(관리코드 | 위치 | 단가) 순으로 정렬
    diffEntries.sort((a, b) => a.key.localeCompare(b.key, "ko"));
    // DIFF 시트에 차이점 행을 추가
    diffEntries.forEach((entry) => appendDiffRows(sheet, entry));
    return workbook;
}
/**
 * @description 주어진 엑셀 파일에서 행 스냅샷을 추출합니다.
 *
 * @param filePath 엑셀 파일 경로
 * @returns {Promise<Map<string, RowSnapshot>>} 행 스냅샷 맵
 */
function buildSnapshotMap(items) {
    const map = new Map();
    items.forEach((item, index) => {
        const values = buildRowValues(item);
        const key = buildUniqueKey(values);
        if (!key) {
            return;
        }
        map.set(key, {
            key,
            rowNumber: index + 2,
            values,
        });
    });
    return map;
}
function buildRowValues(item) {
    const totalPrice = item.totalPrice ?? (item.unitPrice ?? 0) * (item.stockNum ?? 0);
    return {
        종류: item.type,
        분류: item.classification,
        관리코드: item.productCode,
        제품명: item.name,
        모델명: item.model,
        사양: item.spec,
        제조사: item.manufacturer,
        위치: item.spot,
        재고: item.stockNum ?? 0,
        단위: item.quantityUnit ?? "",
        상태: item.conditions,
        단가: item.unitPrice ?? 0,
        합계: totalPrice,
        "총 재고": item.stockNum ?? 0,
        "총 금액": totalPrice,
    };
}
function buildUniqueKey(values) {
    const keyParts = UNIQUE_KEY_HEADERS.map((header) => normalizeExcelValue(values[header]));
    return keyParts.every((part) => part === "") ? "" : keyParts.join(" | ");
}
function collectDiffEntries(oldRows, newRows) {
    const diffEntries = [];
    const mergedKeys = new Set([
        ...oldRows.keys(),
        ...newRows.keys(),
    ]);
    mergedKeys.forEach((key) => {
        const oldRow = oldRows.get(key);
        const newRow = newRows.get(key);
        if (!oldRow || !newRow) {
            diffEntries.push({
                key,
                oldRow,
                newRow,
                changedHeaders: collectMeaningfulHeaders(oldRow, newRow),
            });
            return;
        }
        const changedHeaders = diffHeaders(oldRow, newRow);
        if (changedHeaders.length > 0) {
            diffEntries.push({ key, oldRow, newRow, changedHeaders });
        }
    });
    return diffEntries;
}
function collectMeaningfulHeaders(oldRow, newRow) {
    return excelHeader.filter((header) => {
        if (!header)
            return false;
        const oldVal = normalizeExcelValue(oldRow?.values[header]);
        const newVal = normalizeExcelValue(newRow?.values[header]);
        return oldVal !== "" || newVal !== "";
    });
}
function diffHeaders(oldRow, newRow) {
    return excelHeader.filter((header) => {
        if (!header)
            return false;
        const oldVal = normalizeExcelValue(oldRow.values[header]);
        const newVal = normalizeExcelValue(newRow.values[header]);
        return oldVal !== newVal;
    });
}
/**
 * @description DIFF 시트의 헤더를 구성합니다.
 *
 * @param sheet
 */
function configureDiffHeader(sheet) {
    const headerRowValues = [...DIFF_METADATA_HEADERS, ...excelHeader];
    const headerRow = sheet.addRow(headerRowValues);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };
    headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE6E6E6" },
    };
    sheet.getColumn(1).width = 12;
    sheet.getColumn(2).width = 12;
    sheet.getColumn(3).width = 32;
    excelHeader.forEach((_, index) => {
        sheet.getColumn(DIFF_METADATA_HEADERS.length + index + 1).width = 15;
    });
}
function appendDiffRows(sheet, entry) {
    const entryType = resolveEntryType(entry);
    const highlight = buildHighlightStyles();
    if (entryType === "modified") {
        const oldRow = appendRow(sheet, "OLD", entry.oldRow);
        const newRow = appendRow(sheet, "NEW", entry.newRow);
        highlightChangedCells(entry, oldRow, newRow, highlight.oldFill, highlight.newFill);
    }
    else if (entryType === "added") {
        const placeholderOld = appendPlaceholderRow(sheet, "OLD", entry.newRow?.key ?? "-", "이전 파일에 없음", highlight.oldFill);
        const newRow = appendRow(sheet, "NEW", entry.newRow);
        highlightWholeRow(placeholderOld, highlight.oldFill);
        if (newRow) {
            highlightWholeRow(newRow, highlight.newFill);
        }
    }
    else {
        const oldRow = appendRow(sheet, "OLD", entry.oldRow);
        if (oldRow) {
            highlightWholeRow(oldRow, highlight.oldFill);
        }
        const placeholderNew = appendPlaceholderRow(sheet, "NEW", entry.oldRow?.key ?? "-", "새 파일에 없음", highlight.newFill);
        highlightWholeRow(placeholderNew, highlight.newFill);
    }
    sheet.addRow([]); // 가독성을 위한 빈 줄
}
function appendRow(sheet, label, snapshot) {
    if (!snapshot) {
        return undefined;
    }
    const values = excelHeader.map((header) => formatCellValue(snapshot.values[header]));
    return sheet.addRow([
        label,
        snapshot.rowNumber ?? "-",
        snapshot.key,
        ...values,
    ]);
}
function appendPlaceholderRow(sheet, label, key, note, fill) {
    const valueCells = new Array(excelHeader.length).fill("");
    valueCells[PLACEHOLDER_NOTE_COLUMN_INDEX] = note;
    const row = sheet.addRow([label, "-", key, ...valueCells]);
    row.eachCell((cell, idx) => {
        if (idx <= DIFF_METADATA_HEADERS.length)
            return; // 메타데이터 열은 제외
        cell.fill = fill;
    });
    return row;
}
function resolveEntryType(entry) {
    if (entry.oldRow && entry.newRow) {
        return "modified";
    }
    if (entry.newRow) {
        return "added";
    }
    return "removed";
}
function buildHighlightStyles() {
    return {
        oldFill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF4CCCC" },
        },
        newFill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFD9E1F2" },
        },
    };
}
function highlightChangedCells(entry, oldRow, newRow, oldFill, newFill) {
    entry.changedHeaders.forEach((header) => {
        const headerIndex = excelHeader.indexOf(header);
        if (headerIndex === -1)
            return;
        const columnIndex = DIFF_METADATA_HEADERS.length + headerIndex + 1;
        if (oldRow) {
            oldRow.getCell(columnIndex).fill = oldFill;
        }
        if (newRow) {
            newRow.getCell(columnIndex).fill = newFill;
        }
    });
}
function highlightWholeRow(row, fill) {
    row.eachCell((cell, idx) => {
        if (idx <= DIFF_METADATA_HEADERS.length)
            return; // 메타데이터 열은 제외
        cell.fill = fill;
    });
}
function formatCellValue(value) {
    if (value === undefined || value === null) {
        return "";
    }
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === "object") {
        const formula = value;
        if ("formula" in formula) {
            const result = formula.result;
            if (result instanceof Date) {
                return result;
            }
            if (typeof result === "number" ||
                typeof result === "boolean" ||
                typeof result === "string") {
                return result;
            }
            if (result) {
                return JSON.stringify(result);
            }
            return `=${formula.formula}`;
        }
        return JSON.stringify(value);
    }
    return value;
}
